import { colors, fonts } from 'component-library/__styling/settings'
import {
    SectionHeader,
    Props as SectionHeaderProps,
} from 'component-library/section-header/SectionHeader/SectionHeader'
import { themedValue } from 'component-library/__styling/themed-value'
import { conditionalStyles } from 'component-library/__styling/conditional-styles'
import { Landscape } from 'component-library/cards/Landscape/Landscape'
import { ListCard } from 'component-library/cards/ListCard/ListCard'
import { calcRem } from 'component-library/__styling/style-functions'
import { Theme } from 'component-library/__styling/themes'
import { MediaMode } from 'component-library/cards/CardMedia/CardMedia'
import { CSSObject } from '@emotion/css'
import styled from '@emotion/styled'
import { ThemeMargins } from 'component-library/__styling/settings/metrics'

const StyledListCounterID = 'november_list'

export interface Props {
    heading: string
}

export interface StyledGridRowProps {
    canGrow?: boolean
    hasBackground?: boolean
}

export const GridStyles: CSSObject = {
    display: 'flex',
    width: '100%',
    textAlign: 'left',
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'stretch',
}

export const StyledGridHeaderRow = styled('div')<StyledGridRowProps>(
    { ...GridStyles },
    ({ hasBackground, canGrow, theme }) => ({
        flex: canGrow ? '1 1 auto' : '0 1 auto',

        ...conditionalStyles(hasBackground, {
            padding: theme.cards.outerMargin,
            backgroundColor: colors.white,
        }),
    })
)

export const StyledSectionHeader = styled(SectionHeader)<SectionHeaderProps>(props => ({
    borderBottom: props.heading
        ? themedValue(props.theme, {
              perthnow: `1px solid ${colors.perthnow.greyMarble}`,
              fallback: undefined,
          })
        : undefined,
    alignSelf: 'flex-start',
}))

export const StyledGrid = styled('div')<{
    noStretch?: boolean
    verticalSpacing?: keyof ThemeMargins | undefined
    as: string
}>(
    {
        display: 'flex',
        flexDirection: 'column',
        padding: 0,
        margin: 0,
        width: '100%',
        lineHeight: 1.154,
    },
    ({ theme, verticalSpacing, noStretch }) => ({
        flex: `${noStretch ? 0 : 1} 1 auto`,
        marginBottom: verticalSpacing && calcRem(theme.margins[verticalSpacing]),
    })
)

export const StyledGridRow = styled('div')<StyledGridRowProps>(
    {
        ...GridStyles,
        flexGrow: 1,
    },
    ({ hasBackground }) => ({
        ...conditionalStyles(hasBackground, {
            backgroundColor: colors.white,
        }),
    })
)

export const StyledGridColumn = styled('div')({
    width: '100%',
    maxWidth: '100%',
    display: 'flex',
    flexBasis: '100%',
    flex: '0 0 auto',
    flexWrap: 'nowrap',
    margin: 0,
    alignItems: 'stretch',
})

interface StyledListProps {
    numberedList?: boolean
    removeBorderLast?: boolean // remove border from last item in list
}

export const StyledList = styled('ol')<StyledListProps>(props => ({
    padding: 0,
    margin: 0,
    width: '100%',
    display: 'flex',
    flexDirection: 'column',

    // Remove top padding on the first item
    '&:first-child': {
        paddingTop: themedValue(props.theme, {
            thewest: 0,
            fallback: undefined,
        }),
    },

    '> .LandscapeCard:not(:first-child)': {
        padding: themedValue(props.theme, {
            thewest: `${calcRem(8, 8, 0, 0)}`,
            fallback: 0,
        }),
    },

    // Dividers
    '& > *': {
        ...conditionalStyles(props.theme.kind === 'thewest' || props.theme.kind === 'perthnow', {
            '&:not(:last-child)::after': {
                content: `''`,
                width: '100%',
                height: 1,
                position: 'absolute',
                left: 0,
                bottom: 0,
                backgroundColor: colors.sevennews.greyMarble,
            },
        }),

        ...conditionalStyles(props.theme.kind === 'sevennews', {
            position: 'relative',

            '&::after': {
                content: `''`,
                width: '100%',
                height: 1,
                position: 'absolute',
                left: 0,
                bottom: 0,
                backgroundColor: colors.sevennews.greyMarble,
            },
        }),
    },

    ...conditionalStyles(props.numberedList, {
        counterReset: StyledListCounterID,
    }),
}))

const listItemStyles = (props: StyledListProps & { theme: Theme }) => ({
    ...conditionalStyles(props.numberedList, {
        display: 'flex',
        flexWrap: 'nowrap',
        border: 'none',

        '&::before': {
            display: 'block',
            padding: calcRem(0, 12, 0, 0),
            fontStyle: 'italic',
            fontFamily: fonts.thewest.serif,
            fontWeight: 400,
            color: colors.thewest.greyQuokka,
            fontSize: calcRem(50),
            lineHeight: 1,
            counterIncrement: StyledListCounterID,
            content: `counter(${StyledListCounterID})`,
            transform: 'translateY(-5px)',
        },
    }),
})

interface StyledLandscapeItemProps {
    numberedList?: boolean
    removeBorderLast?: boolean // remove border from last item in list
    mediaMode?: MediaMode
}

export const StyledLandscapeItem = styled(Landscape)<StyledLandscapeItemProps>(props => ({
    ...listItemStyles(props),
    padding: calcRem(8, 8, 0, 0),

    '&:first-of-type div': {
        paddingTop: themedValue(props.theme, {
            thewest: 0,
            fallback: undefined,
        }),
    },
}))

export const StyledListCardItem = styled(ListCard)<StyledListProps>(props => ({
    ...listItemStyles(props),
}))
